// src/components/common/Loading.jsx
import React, { useMemo } from "react";
import Lottie from "lottie-react";
import loaderJson from "../../assets/icons/Loader.json";

/**
 * Loading (Lottie) component
 *
 * Props:
 * - size: number (px) or string (e.g. "48px") - controls width/height of animation container
 * - loop: boolean (default: true)
 * - speed: number (default: 1)
 * - className: extra wrapper classes
 * - ariaLabel: accessibility label
 *
 * Usage: <Loading size={48} loop speed={1} />
 */
export default function Loading({
  size = 48,
  loop = true,
  speed = 1,
  className = "",
  ariaLabel = "Loading",
}) {
  // memoize animation data to avoid re-parsing JSON on every render
  const animationData = useMemo(() => loaderJson, []);

  // normalize size to css string
  const sizeCss = typeof size === "number" ? `${size}px` : size;

  // wrapper styles keep layout stable
  const wrapperStyle = {
    width: sizeCss,
    height: sizeCss,
    display: "inline-block",
    verticalAlign: "middle",
  };

  // Lottie options
  const lottieProps = {
    animationData,
    loop,
    autoplay: true,
    style: { width: "100%", height: "100%" },
  };

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={`loading-lottie-wrapper ${className}`}
      style={wrapperStyle}
    >
      {/* try Lottie; if it fails the fallback below will show in console (rare) */}
      <Lottie
        {...lottieProps}
        rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
        speed={speed}
      />
    </span>
  );
}
